// One-sided bracket layout.
//
// Every match is placed from the tree itself, not from its round position: a
// match sits at the average of its two sides' centres, where a side is either
// the subtree feeding it or one empty slot. That places the 21-leaf WP Fours
// tree (5 play-offs into selected Last16 slots) correctly and leaves the
// standard trees unchanged. Columns run left to right in round order.
//
// Units are half a pitch: `pitch = cardHeight + gap`, so `unit = pitch / 2`
// and a single empty slot is one unit. Adjacent matches in a column are at
// least two units apart, which is exactly one pitch - cards cannot overlap.

export const roundNumberOf = (match) => {
  const value = Number(String(match?.tournamentRoundText || '').replace(/\D/g, ''));
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const fallbackLayout = (matches, cardHeight, gap) => {
  const pitch = cardHeight + gap;
  const rounds = new Map();

  matches.forEach((match, index) => {
    const round = roundNumberOf(match);
    if (!rounds.has(round)) {
      rounds.set(round, []);
    }
    rounds.get(round).push({ match, index });
  });

  const positions = {};
  let bottom = 0;

  Array.from(rounds.entries())
    .sort((a, b) => a[0] - b[0])
    .forEach(([, entries]) => {
      entries
        .slice()
        .sort((a, b) => a.index - b.index)
        .forEach((entry, row) => {
          const top = row * pitch;
          positions[entry.match.id] = top;
          bottom = Math.max(bottom, top + cardHeight);
        });
    });

  return { positions, height: bottom + gap, valid: false };
};

// Which side of a destination each feeder occupies. The winnerOf placeholder
// and the advanced copy both carry that side; a legacy bracket with neither
// falls back to the remaining slots in declaration order, which is how the
// existing section and PBA brackets were laid out.
const sidesForMatch = (match, feederIds) => {
  const slots = [null, null];
  const unassigned = [];
  const participants = Array.isArray(match.participants) ? match.participants : [];

  feederIds.forEach((feederId) => {
    let slot = participants.findIndex(
      (participant) => participant && participant.id === `winner-of-${feederId}`
    );

    if (slot !== 0 && slot !== 1) {
      slot = participants.findIndex(
        (participant) => participant && participant.sourceMatch === feederId
      );
    }

    if ((slot === 0 || slot === 1) && !slots[slot]) {
      slots[slot] = feederId;
    } else {
      unassigned.push(feederId);
    }
  });

  unassigned.forEach((feederId) => {
    const free = slots[0] === null ? 0 : 1;
    slots[free] = feederId;
  });

  return slots;
};

export const buildBracketLayout = ({ matches, cardHeight = 0, gap = 80 }) => {
  const list = Array.isArray(matches) ? matches : [];
  const height = Math.max(0, Number(cardHeight) || 0);

  if (list.length === 0) {
    return { positions: {}, height: 0, valid: true };
  }

  const byId = new Map();
  for (const match of list) {
    if (!match?.id || byId.has(match.id)) {
      return fallbackLayout(list, height, gap);
    }
    byId.set(match.id, match);
  }

  const predecessors = new Map();
  const terminals = [];

  for (const match of list) {
    const nextId = match.nextMatchId;
    if (!nextId || !byId.has(nextId)) {
      terminals.push(match.id);
      continue;
    }

    if (!predecessors.has(nextId)) {
      predecessors.set(nextId, []);
    }
    const feeders = predecessors.get(nextId);
    if (feeders.includes(match.id)) {
      return fallbackLayout(list, height, gap);
    }
    feeders.push(match.id);
    if (feeders.length > 2) {
      return fallbackLayout(list, height, gap);
    }
  }

  if (terminals.length !== 1) {
    return fallbackLayout(list, height, gap);
  }

  const slots = new Map();
  list.forEach((match) => {
    slots.set(match.id, sidesForMatch(match, predecessors.get(match.id) || []));
  });

  // Bottom-up: the leaf count and relative centre of each subtree. A side that
  // is fed uses the child's centre; an empty side is one unit. The match centre
  // is the average of the two sides, never the span midpoint - with unequal
  // spans (5 play-offs into 8 Last16 slots) the midpoint draws the elbows to
  // the wrong place.
  const shapes = new Map();
  const shape = (matchId, visiting) => {
    if (shapes.has(matchId)) {
      return shapes.get(matchId);
    }
    if (visiting.has(matchId)) {
      throw new Error('The bracket tree contains a loop');
    }

    const visit = new Set(visiting);
    visit.add(matchId);

    let cursor = 0;
    const sides = slots.get(matchId).map((feederId) => {
      if (feederId) {
        const child = shape(feederId, visit);
        const side = { feederId, count: child.count, centre: cursor + child.centre };
        cursor += child.count;
        return side;
      }

      const side = { feederId: null, count: 1, centre: cursor + 0.5 };
      cursor += 1;
      return side;
    });

    const info = {
      count: cursor,
      centre: (sides[0].centre + sides[1].centre) / 2,
      sides
    };
    shapes.set(matchId, info);
    return info;
  };

  try {
    shape(terminals[0], new Set());
  } catch (error) {
    return fallbackLayout(list, height, gap);
  }

  // A match not reachable from the terminal (a second, disconnected tree)
  // cannot be placed relative to it.
  if (shapes.size !== list.length) {
    return fallbackLayout(list, height, gap);
  }

  const pitch = height + gap;
  const unit = pitch / 2;
  const positions = {};

  // Top-down: turn each subtree-relative centre into an absolute position.
  const place = (matchId, offset) => {
    const info = shapes.get(matchId);
    positions[matchId] = (offset + info.centre) * unit - height / 2;

    let cursor = offset;
    info.sides.forEach((side) => {
      if (side.feederId) {
        place(side.feederId, cursor);
        cursor += side.count;
      } else {
        cursor += 1;
      }
    });
  };

  place(terminals[0], 0);

  let bottom = 0;
  Object.values(positions).forEach((top) => {
    bottom = Math.max(bottom, top + height);
  });

  return {
    positions,
    height: bottom + gap,
    valid: true
  };
};

export default buildBracketLayout;
